import { useTranslation } from "react-i18next";

import type { Activity, Contact } from "@/api/queries";
import { useCompanyContacts, useContact } from "@/api/queries";
import { ContactRecordingTag } from "@/components/CompanyTags";
import { Spinner, Tag } from "@/components/ui";
import { ContactReach } from "@/empresas/ContactReach";

/**
 * *Contactar*, inside the event modal: the follow-up is done from where it is
 * read. A scheduled call has *Llamar* beside the number it is for, and the
 * same row offers WhatsApp and the address offers *Enviar correo* — every
 * channel, not the one the activity's kind names, because a seller who planned
 * a call and finds the line busy writes instead.
 *
 * Who: the contact the activity names, or — when it names none, which is most
 * of the imported history — everyone still in use at the company.
 *
 * The buttons and their rules are `ContactReach`'s, the ones the contact card
 * uses. The call they start is run from the modal's own footer, which carries
 * the call bar — see `DetalleEvento`.
 */
export function Contactar({ activity }: { activity: Activity }) {
  const { t } = useTranslation();

  // Cold prospecting: nobody on file to reach.
  if (activity.contact == null && activity.company == null) return null;

  return (
    <section className="mt-6 border-t border-rule pt-5">
      <p className="kicker mb-3 text-mute-600">{t("evento.reach")}</p>
      {activity.contact != null ? (
        <NamedContact id={activity.contact} />
      ) : (
        <CompanyPeople companyId={activity.company!} />
      )}
    </section>
  );
}

function NamedContact({ id }: { id: number }) {
  const { t } = useTranslation();
  const contact = useContact(id);

  if (contact.isPending) return <Spinner label={t("common.loading")} />;
  if (contact.isError || contact.data === undefined) {
    return <p className="text-[13px] text-mute-600">{t("errors.contact")}</p>;
  }
  return <Person contact={contact.data} />;
}

function CompanyPeople({ companyId }: { companyId: number }) {
  const { t } = useTranslation();
  const contacts = useCompanyContacts(companyId);
  // Only the ones still in use: a disabled contact has nothing to be reached
  // by, and here that is all a row is for.
  const people = (contacts.data?.results ?? []).filter(
    (contact) => !contact.disabled_at,
  );

  if (contacts.isPending) return <Spinner label={t("common.loading")} />;
  if (contacts.isError) {
    return <p className="text-[13px] text-mute-600">{t("companies.contactsFailed")}</p>;
  }
  if (people.length === 0) {
    return <p className="text-[13px] text-mute-600">{t("evento.noPeople")}</p>;
  }
  return (
    <div className="flex flex-col gap-5">
      {people.map((contact) => (
        <Person key={contact.id} contact={contact} />
      ))}
    </div>
  );
}

function Person({ contact }: { contact: Contact }) {
  const { t } = useTranslation();
  const reachable = contact.phones.length > 0 || contact.emails.length > 0;

  return (
    <div>
      <p className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <span className="font-display text-sm font-extrabold">{contact.name}</span>
        {contact.job_title ? (
          <span className="text-xs text-mute-600">{contact.job_title}</span>
        ) : null}
        {contact.disabled_at ? (
          <Tag
            tone="outline"
            className="text-[10px] tracking-[0.06em] uppercase"
            title={t("companyDetail.contactDisabledHint")}
          >
            {t("companyDetail.contactDisabled")}
          </Tag>
        ) : (
          // Whether to mention the recording, right beside the number to dial.
          <ContactRecordingTag answer={contact.consents_to_recording} />
        )}
      </p>
      {reachable ? (
        <ContactReach contact={contact} />
      ) : (
        <p className="mt-1.5 text-[13px] text-mute-600">{t("evento.noChannels")}</p>
      )}
    </div>
  );
}
